import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
  useToast
} from '@chakra-ui/react';
import { FiGlobe, FiCheck } from 'react-icons/fi';
import { useUpdatePlanInfo } from '../services/planService';
import { getSupportedCountries, getCurrencyForCountry, validateCountrySelection } from '../utils/currency';

const CountrySelectionModal = ({ isOpen, onClose, onSuccess }) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const { updatePlanInfoPayload, updatePlanInfoIsLoading } = useUpdatePlanInfo((res) => {
    setIsSubmitting(false);
    toast({
      title: "Profile Updated",
      description: "Your country and phone information has been saved successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    onSuccess?.(res);
    onClose();
  });

  const supportedCountries = getSupportedCountries();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCountry) {
      toast({
        title: "Selection Required",
        description: "Please select your country to continue.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      country: selectedCountry === 'Other' ? 'United States' : selectedCountry,
      phone: phoneNumber || null
    };

    updatePlanInfoPayload(payload);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };

  const getSelectedCurrencyInfo = () => {
    if (!selectedCountry) return null;
    return getCurrencyForCountry(selectedCountry === 'Other' ? 'United States' : selectedCountry);
  };

  const currencyInfo = getSelectedCurrencyInfo();
  const validation = validateCountrySelection(selectedCountry);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <FiGlobe size={24} />
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">
                Select Your Country
              </Text>
              <Text fontSize="sm" color="gray.500">
                This helps us set the correct currency for your payroll
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Country Selection */}
              <FormControl isRequired>
                <FormLabel>Country</FormLabel>
                <Select
                  placeholder="Select your country"
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  size="lg"
                >
                  {supportedCountries.map((country) => (
                    <option key={country.name} value={country.name}>
                      {country.name} - {country.currency}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Currency Preview */}
              {currencyInfo && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Currency Preview</AlertTitle>
                    <AlertDescription>
                      Your payroll will use: <strong>{currencyInfo.symbol} {currencyInfo.name}</strong>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              <Divider />

              {/* Phone Number (Optional) */}
              <FormControl>
                <FormLabel>Phone Number (Optional)</FormLabel>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  size="lg"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This helps us provide better support for your account
                </Text>
              </FormControl>

              {/* Validation Message */}
              {selectedCountry && (
                <Alert 
                  status={validation.isValid ? "success" : "warning"} 
                  borderRadius="md"
                >
                  <AlertIcon />
                  <AlertDescription>
                    {validation.message}
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} width="full">
              <Button
                variant="outline"
                onClick={onClose}
                flex={1}
                isDisabled={isSubmitting || updatePlanInfoIsLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                flex={1}
                isLoading={isSubmitting || updatePlanInfoIsLoading}
                loadingText="Saving..."
                leftIcon={<FiCheck />}
                isDisabled={!selectedCountry}
              >
                Save & Continue
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CountrySelectionModal;